import type { Account, Currency } from '../../types/account';
import { compareAccountsForTree } from '../../lib/accountSort';

export type AccountTreeNode = {
  account: Account;
  depth: number;
  resolvedCurrency: Currency;
  children: AccountTreeNode[];
};

// Build a forest from a flat list of accounts. Roots (parentAccountId === null)
// sit at the top; children attach under their parents. Each node carries its
// depth and inherited currency (walked once, stored per-node).
//
// Sort inside each parent follows src/lib/accountSort.ts: groups / brands /
// head_office / region / dealership before locations, alphabetical inside
// each type band.
export function buildAccountTree(accounts: Account[]): AccountTreeNode[] {
  const byParent = new Map<string | null, Account[]>();
  for (const a of accounts) {
    const key = a.parentAccountId;
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key)!.push(a);
  }
  for (const list of byParent.values()) {
    list.sort(compareAccountsForTree);
  }

  const build = (parentId: string | null, depth: number, inheritedCurrency: Currency | null): AccountTreeNode[] => {
    const kids = byParent.get(parentId) ?? [];
    return kids.map((account) => {
      const currency: Currency = account.currency ?? inheritedCurrency ?? 'AUD';
      return {
        account,
        depth,
        resolvedCurrency: currency,
        children: build(account.id, depth + 1, currency),
      };
    });
  };
  return build(null, 0, null);
}

export function flattenTree(nodes: AccountTreeNode[]): AccountTreeNode[] {
  const out: AccountTreeNode[] = [];
  const walk = (n: AccountTreeNode) => {
    out.push(n);
    for (const c of n.children) walk(c);
  };
  for (const n of nodes) walk(n);
  return out;
}
